import { FormEvent, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { FullScreenSpinner } from '../components/ui/Spinner';
import { useAuth } from '../contexts/AuthContext';

function translateAuthError(code: string): string {
  switch (code) {
    case 'auth/invalid-email':
      return 'E-mail inválido.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'E-mail ou senha incorretos.';
    case 'auth/user-disabled':
      return 'Esta conta foi desativada. Contate um administrador.';
    case 'auth/popup-closed-by-user':
      return 'Login com Google cancelado.';
    default:
      return 'Não foi possível entrar. Tente novamente.';
  }
}

export function Login() {
  const { user, initializing, signIn, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const showGoogle = false;

  if (initializing) return <FullScreenSpinner />;
  if (user) return <Navigate to="/patients" replace />;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(email.trim(), password);
    } catch (err) {
      setError(translateAuthError((err as { code?: string }).code ?? ''));
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    setError(null);
    setBusy(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(translateAuthError((err as { code?: string }).code ?? ''));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="brand" style={{ padding: 0, marginBottom: 24 }}>
          <span className="logo"> </span>
          <span style={{ fontSize: 18, fontWeight: 700 }}>
            Checklist ESDM — Painel
          </span>
        </div>
        <p className="muted" style={{ marginTop: 0, marginBottom: 24 }}>
          Acesse para gerenciar pacientes e o Checklist ESDM.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="field">
            <label htmlFor="email">E-mail</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>
          <div className="field">
            <label htmlFor="password">Senha</label>
            <div className="password-field">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="field error" style={{ marginTop: -8 }}>
              {error}
            </div>
          )}

          <button
            className="btn"
            type="submit"
            disabled={busy}
            style={{ width: '100%', marginTop: 8 }}
          >
            {busy ? 'Entrando…' : 'Entrar'}
          </button>
        </form>
        {showGoogle && (
          <>
            <div
              className="row"
              style={{
                margin: '20px 0',
                color: 'var(--text-secondary)',
                fontSize: 13
              }}
            >
              <div
                style={{ flex: 1, height: 1, background: 'var(--border)' }}
              />
              ou
              <div
                style={{ flex: 1, height: 1, background: 'var(--border)' }}
              />
            </div>

            <button
              className="btn secondary"
              type="button"
              onClick={handleGoogle}
              disabled={busy}
              style={{ width: '100%' }}
            >
              Entrar com Google
            </button>
          </>
        )}
      </div>
    </div>
  );
}
